import React from "react";
import {
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  AlignLeft,
  AlignCenter,
  AlignRight,
  X,
} from "lucide-react";
import type { ResponsiveSizes } from "../types";

interface ResponsiveTableSizeModalProps {
  isOpen: boolean;
  responsiveTableWidths: ResponsiveSizes;
  tableWidthType: "percent" | "pixel";
  enableHorizontalScroll: boolean;
  tableAlignment: "left" | "center" | "right";
  onTableWidthsChange: (widths: ResponsiveSizes) => void;
  onWidthTypeChange: (type: "percent" | "pixel") => void;
  onHorizontalScrollChange: (enabled: boolean) => void;
  onAlignmentChange: (alignment: "left" | "center" | "right") => void;
  onApply: () => void;
  onClose: () => void;
}

const percentOptions = [
  { value: "", label: "None" },
  { value: "25", label: "25%" },
  { value: "33", label: "33%" },
  { value: "50", label: "50%" },
  { value: "66", label: "66%" },
  { value: "75", label: "75%" },
  { value: "80", label: "80%" },
  { value: "90", label: "90%" },
  { value: "100", label: "100%" },
];

export const ResponsiveTableSizeModal: React.FC<ResponsiveTableSizeModalProps> = ({
  isOpen,
  responsiveTableWidths,
  tableWidthType,
  enableHorizontalScroll,
  tableAlignment,
  onTableWidthsChange,
  onWidthTypeChange,
  onHorizontalScrollChange,
  onAlignmentChange,
  onApply,
  onClose,
}) => {
  if (!isOpen) return null;

  const breakpoints = [
    { key: "xs", label: "Mobile (xs) - Base", icon: Smartphone, placeholder: "e.g., 400" },
    { key: "sm", label: "Tablet (sm) - 1080px", icon: Tablet, placeholder: "e.g., 600" },
    { key: "md", label: "Desktop (md) - 1230px", icon: Laptop, placeholder: "e.g., 800" },
    { key: "lg", label: "Large (lg) - 1430px", icon: Monitor, placeholder: "e.g., 1000" },
    { key: "xl", label: "Extra Large (xl) - 1750px", icon: Monitor, placeholder: "e.g., 1200" },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Responsive Table Size
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Set different table widths for different screen sizes. Choose between percentage or fixed
          pixel widths.
        </p>

        <div className="flex flex-col gap-4">
          {/* Width Type Toggle */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Width Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onWidthTypeChange("percent");
                  onTableWidthsChange({ xs: "", sm: "", md: "", lg: "", xl: "" });
                }}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  tableWidthType === "percent"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Percentage (%)
              </button>
              <button
                type="button"
                onClick={() => {
                  onWidthTypeChange("pixel");
                  onTableWidthsChange({ xs: "", sm: "", md: "", lg: "", xl: "" });
                }}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  tableWidthType === "pixel"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Fixed (px)
              </button>
            </div>
          </div>

          {/* Horizontal Scroll Option */}
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-md">
            <input
              type="checkbox"
              id="horizontalScroll"
              checked={enableHorizontalScroll}
              onChange={(e) => onHorizontalScrollChange(e.target.checked)}
              className="w-4 h-4 text-primary-main border-gray-300 rounded focus:ring-primary-main"
            />
            <label htmlFor="horizontalScroll" className="text-sm text-gray-700">
              Enable horizontal scroll on mobile when table overflows
            </label>
          </div>

          {/* Table Alignment */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Table Alignment</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onAlignmentChange("left")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  tableAlignment === "left"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <AlignLeft className="w-4 h-4" />
                Left
              </button>
              <button
                type="button"
                onClick={() => onAlignmentChange("center")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  tableAlignment === "center"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <AlignCenter className="w-4 h-4" />
                Center
              </button>
              <button
                type="button"
                onClick={() => onAlignmentChange("right")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  tableAlignment === "right"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <AlignRight className="w-4 h-4" />
                Right
              </button>
            </div>
          </div>

          {/* Breakpoint Width Inputs */}
          {breakpoints.map(({ key, label, icon: Icon, placeholder }) => (
            <div key={key} className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {label}
              </label>
              {tableWidthType === "percent" ? (
                <select
                  value={responsiveTableWidths[key]}
                  onChange={(e) =>
                    onTableWidthsChange({ ...responsiveTableWidths, [key]: e.target.value })
                  }
                  className="border border-gray-300 px-3 py-2 rounded-md text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
                >
                  {percentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  min="100"
                  max="2000"
                  placeholder={placeholder}
                  value={responsiveTableWidths[key]}
                  onChange={(e) =>
                    onTableWidthsChange({ ...responsiveTableWidths, [key]: e.target.value })
                  }
                  className="border border-gray-300 px-3 py-2 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
                />
              )}
            </div>
          ))}

          {/* Preview */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview</h4>
            <div
              className={`bg-gray-100 p-4 rounded ${enableHorizontalScroll ? "overflow-x-auto" : ""}`}
            >
              <div
                className="bg-white border border-gray-300 p-2 text-center text-sm transition-all"
                style={{
                  width:
                    tableWidthType === "pixel"
                      ? `${responsiveTableWidths.xs || responsiveTableWidths.sm || responsiveTableWidths.md || "400"}px`
                      : `${responsiveTableWidths.xs || responsiveTableWidths.sm || responsiveTableWidths.md || "100"}%`,
                  minWidth:
                    tableWidthType === "pixel"
                      ? `${responsiveTableWidths.xs || "400"}px`
                      : undefined,
                  marginLeft:
                    tableAlignment === "center"
                      ? "auto"
                      : tableAlignment === "right"
                        ? "auto"
                        : "0",
                  marginRight:
                    tableAlignment === "center" ? "auto" : tableAlignment === "right" ? "0" : "auto",
                }}
              >
                Table at{" "}
                {responsiveTableWidths.xs ||
                  responsiveTableWidths.sm ||
                  responsiveTableWidths.md ||
                  (tableWidthType === "pixel" ? "400" : "100")}
                {tableWidthType === "pixel" ? "px" : "%"} width
                <div className="text-xs text-gray-500 mt-1">
                  Aligned: {tableAlignment}
                  {enableHorizontalScroll && " • scroll enabled"}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


